import type { GpsBody } from '@ipc/shared'

/**
 * Location v2 collection (docs/specs/2026-10-26-location-v2-design.md "Device location collection"): one
 * `watchPosition` that keeps the most accurate fresh fix, stops at TARGET_M or at the deadline, and reports progress.
 * Phones often answer first with a coarse network fix and improve over a few seconds; a single getCurrentPosition
 * took that first answer (the v1 "2000 km away" report).
 */

export const TARGET_M = 100
export const DEADLINE_MS = 30_000
/** A sample older than this when it arrives (a cached fix) is recorded but never chosen. */
export const MAX_SAMPLE_AGE_MS = 120_000
export const MAX_SAMPLES = 20

export type GeoError = 'denied' | 'unavailable' | 'timeout' | 'unsupported'

export interface FixSample {
  lat: number
  lng: number
  accuracyM: number
  /** position.timestamp (device clock). */
  timestamp: number
  /** Date.now() when the sample arrived (same clock). */
  receivedAt: number
  hasAltitude: boolean
  hasSpeed: boolean
}

export interface WatchResult {
  best: FixSample | null
  first: FixSample | null
  samples: FixSample[]
  elapsedMs: number
  /** Why there is no fix (null when there is one). */
  error: GeoError | null
}

export interface WatchOptions {
  targetM?: number
  deadlineMs?: number
  maxSampleAgeMs?: number
  /** Called with the best fix so far after every sample. */
  onProgress?: (best: FixSample | null) => void
  now?: () => number
}

export interface Watch {
  promise: Promise<WatchResult>
  /** Stops the watch now (the promise resolves with what it has). */
  stop(): void
}

export function watchBestFix(geo: Geolocation | undefined | null, opts: WatchOptions = {}): Watch {
  const now = opts.now ?? (() => Date.now())
  const target = opts.targetM ?? TARGET_M
  const maxAge = opts.maxSampleAgeMs ?? MAX_SAMPLE_AGE_MS
  const started = now()
  const samples: FixSample[] = []
  let first: FixSample | null = null
  let best: FixSample | null = null
  let lastError: GeoError | null = null
  let done = false
  let watchId: number | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let resolve!: (r: WatchResult) => void
  const promise = new Promise<WatchResult>((r) => (resolve = r))

  const finish = (error: GeoError | null) => {
    if (done) return
    done = true
    if (timer) clearTimeout(timer)
    if (watchId !== null && geo) geo.clearWatch(watchId)
    resolve({
      best,
      first,
      samples: [...samples],
      elapsedMs: Math.max(0, now() - started),
      error: best ? null : (error ?? lastError ?? 'timeout'),
    })
  }

  if (!geo || typeof geo.watchPosition !== 'function') {
    finish('unsupported')
    return { promise, stop: () => undefined }
  }

  timer = setTimeout(() => finish(null), opts.deadlineMs ?? DEADLINE_MS)
  try {
    watchId = geo.watchPosition(
      (pos) => {
        if (done) return
        const c = pos.coords
        const s: FixSample = {
          lat: c.latitude,
          lng: c.longitude,
          accuracyM: Number.isFinite(c.accuracy) && c.accuracy > 0 ? c.accuracy : 100_000,
          timestamp: typeof pos.timestamp === 'number' && pos.timestamp > 0 ? pos.timestamp : now(),
          receivedAt: now(),
          hasAltitude: c.altitude !== null && c.altitude !== undefined,
          hasSpeed: c.speed !== null && c.speed !== undefined && !Number.isNaN(c.speed),
        }
        first ??= s
        samples.push(s)
        if (samples.length > MAX_SAMPLES) samples.shift()
        const fresh = s.receivedAt - s.timestamp <= maxAge
        if (fresh && (best === null || s.accuracyM < best.accuracyM)) best = s
        opts.onProgress?.(best)
        if (best && best.accuracyM <= target) finish(null)
      },
      (err) => {
        if (done) return
        if (err.code === 1) return finish('denied')
        // Unavailable / timeout: the watch keeps trying until the deadline.
        lastError = err.code === 3 ? 'timeout' : 'unavailable'
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: opts.deadlineMs ?? DEADLINE_MS }
    )
  } catch {
    finish('unsupported')
  }
  return { promise, stop: () => finish(null) }
}

const ROUND_ACCURACY = (a: number) => Number.isInteger(a) && (a <= 1 || a % 5 === 0)

/** Soft spoof signals (audit 12): shown to the admin, never a reason. */
export function suspectSignals(r: WatchResult, geo: Geolocation | undefined | null): string[] {
  const out: string[] = []
  const s = r.samples
  if (
    s.length >= 3 &&
    s.every((x) => x.lat === s[0]!.lat && x.lng === s[0]!.lng && x.accuracyM === s[0]!.accuracyM) &&
    ROUND_ACCURACY(s[0]!.accuracyM)
  )
    out.push('constant_samples')
  try {
    if (geo && !/\[native code\]/.test(Function.prototype.toString.call(geo.watchPosition)))
      out.push('non_native_geolocation')
  } catch {
    // ignore
  }
  return out
}

/** The POST body for a finished watch with a fix. */
export function gpsBodyOf(
  r: WatchResult & { best: FixSample },
  placeId: number,
  geo: Geolocation | undefined | null,
  now: number = Date.now()
): GpsBody {
  let timeZone: string | undefined
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    timeZone = undefined
  }
  const best = r.best
  return {
    placeId,
    lat: best.lat,
    lng: best.lng,
    accuracyM: Math.min(Math.max(best.accuracyM, 1), 10_000_000),
    ageMs: Math.max(0, Math.round(now - best.timestamp)),
    ...(timeZone ? { timeZone: timeZone.slice(0, 64) } : {}),
    ...(r.first ? { firstAccuracyM: Math.min(Math.max(r.first.accuracyM, 1), 10_000_000) } : {}),
    elapsedMs: Math.min(Math.round(r.elapsedMs), 600_000),
    samples: r.samples.map((s) => ({
      accuracyM: Math.min(Math.max(s.accuracyM, 1), 10_000_000),
      ageMs: Math.min(Math.max(Math.round(s.receivedAt - s.timestamp), -60_000), 86_400_000),
      hasAltitude: s.hasAltitude,
      hasSpeed: s.hasSpeed,
    })),
    suspect: suspectSignals(r, geo),
  }
}

/** "±850 m", "±2.5 km", "±12 km". */
export function fmtAccuracy(m: number): string {
  if (m < 1000) return `±${Math.round(m)} m`
  const km = m / 1000
  return `±${km < 10 ? km.toFixed(1) : Math.round(km)} km`
}

export function deviceTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}
